/**
 * 内容编辑页面 - 完整标书预览和生成
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { OutlineData, OutlineItem } from '../types';
import { DocumentTextIcon, PlayIcon, DocumentArrowDownIcon, CheckCircleIcon, ExclamationCircleIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import {
  collectSseText,
  contentApi,
  ChapterContentRequest,
  documentApi,
  getErrorMessage,
  projectApi,
  ProjectSettings,
} from '../services/api';
import { saveAs } from 'file-saver';
import { draftStorage } from '../utils/draftStorage';

interface ContentEditProps {
  projectId?: number | null;
  projectName?: string;
  outlineData: OutlineData | null;
  settings?: ProjectSettings;
}

interface GenerationProgress {
  total: number;
  completed: number;
  current: string;
  failed: string[];
  generating: Set<string>; // 正在生成的项目ID集合
}

const getContentScrollContainer = () =>
  document.getElementById('visual-workspace-scroll')
  || document.getElementById('app-main-scroll');

const ContentEdit: React.FC<ContentEditProps> = ({
  projectId,
  projectName,
  outlineData,
  settings,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [progress, setProgress] = useState<GenerationProgress>({
    total: 0,
    completed: 0,
    current: '',
    failed: draftStorage.loadDraft()?.failedChapterIds || [],
    generating: new Set<string>()
  });
  const [leafItems, setLeafItems] = useState<OutlineItem[]>([]);
  const [activeChapterId, setActiveChapterId] = useState(
    draftStorage.loadDraft()?.lastEditedChapterId || ''
  );
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolvedProjectName = outlineData?.project_name || projectName || '';

  useEffect(() => {
    // #region debug-point D:content-state-observed
    if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
      void fetch('http://127.0.0.1:7783/event', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: 'export-project-name',
          runId: 'post-fix',
          hypothesisId: 'D',
          location: 'frontend/src/pages/ContentEdit.tsx:state-observed',
          msg: '[DEBUG] Content editor observed outline state',
          data: {
            projectId: projectId || null,
            outlineProjectName: outlineData?.project_name || '',
            fallbackProjectName: projectName || '',
            resolvedProjectName,
            outlineCount: outlineData?.outline.length || 0,
          },
          ts: Date.now(),
        }),
      }).catch(() => undefined);
    }
    // #endregion
  }, [outlineData, projectId, projectName, resolvedProjectName]);

  // 收集所有叶子节点
  const collectLeafItems = useCallback((items: OutlineItem[]): OutlineItem[] => {
    let leaves: OutlineItem[] = [];
    items.forEach(item => {
      if (!item.children || item.children.length === 0) {
        leaves.push(item);
      } else {
        leaves = leaves.concat(collectLeafItems(item.children));
      }
    });
    return leaves;
  }, []);

  // 获取章节的上级章节信息
  const getParentChapters = useCallback((targetId: string, items: OutlineItem[], parents: OutlineItem[] = []): OutlineItem[] => {
    for (const item of items) {
      if (item.id === targetId) {
        return parents;
      }
      if (item.children && item.children.length > 0) {
        const found = getParentChapters(targetId, item.children, [...parents, item]);
        if (found.length > 0 || item.children.some(child => child.id === targetId)) {
          return found.length > 0 ? found : [...parents, item];
        }
      }
    }
    return [];
  }, []);

  // 获取章节的同级章节信息
  const getSiblingChapters = useCallback((targetId: string, items: OutlineItem[]): OutlineItem[] => {
    // 直接在当前级别查找
    if (items.some(item => item.id === targetId)) {
      return items;
    }
    
    // 递归在子级别查找
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const siblings = getSiblingChapters(targetId, item.children);
        if (siblings.length > 0) {
          return siblings;
        }
      }
    }
    
    return [];
  }, []);

  useEffect(() => {
    if (outlineData) {
      const leaves = collectLeafItems(outlineData.outline);
      // 恢复本地缓存的正文内容（仅对叶子节点生效）
      const filtered = draftStorage.filterContentByOutlineLeaves(outlineData.outline);
      const mergedLeaves = leaves.map((leaf) => {
        const cached = filtered[leaf.id];
        return leaf.content ? leaf : cached ? { ...leaf, content: cached } : leaf;
      });

      // 目录变更时，顺手清理掉无效的旧缓存（只保留当前叶子节点）
      draftStorage.saveContentById(filtered);

      const leafIds = new Set(leaves.map((leaf) => leaf.id));
      const savedDraft = draftStorage.loadDraft();
      const failedChapterIds = (savedDraft?.failedChapterIds || []).filter(
        (chapterId) => leafIds.has(chapterId)
      );
      setLeafItems(mergedLeaves);
      setProgress(prev => ({
        ...prev,
        total: leaves.length,
        failed: failedChapterIds,
      }));
      if (
        savedDraft?.lastEditedChapterId
        && leafIds.has(savedDraft.lastEditedChapterId)
      ) {
        setActiveChapterId(savedDraft.lastEditedChapterId);
      }
    }
  }, [outlineData, collectLeafItems]);

  useEffect(() => {
    if (leafItems.length === 0) return;
    const savedDraft = draftStorage.loadDraft();
    const timer = window.setTimeout(() => {
      const chapterId = savedDraft?.lastEditedChapterId;
      const chapterElement = chapterId
        ? document.getElementById(`content-chapter-${chapterId}`)
        : null;
      if (chapterElement && typeof chapterElement.scrollIntoView === 'function') {
        chapterElement.scrollIntoView({ block: 'start' });
        return;
      }
      const scrollContainer = getContentScrollContainer();
      if (scrollContainer && savedDraft?.contentScrollTop !== undefined) {
        scrollContainer.scrollTo({ top: savedDraft.contentScrollTop });
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [leafItems.length]);

  // 监听页面滚动，控制回到顶部按钮的显示
  useEffect(() => {
    const scrollContainer = getContentScrollContainer();

    const handleScroll = () => {
      const scrollTop = scrollContainer
        ? scrollContainer.scrollTop
        : (window.pageYOffset || document.documentElement.scrollTop);
      setShowScrollToTop(scrollTop > 300);
      if (scrollSaveTimer.current) {
        clearTimeout(scrollSaveTimer.current);
      }
      scrollSaveTimer.current = setTimeout(() => {
        draftStorage.saveDraft({ contentScrollTop: scrollTop });
      }, 150);
    };

    handleScroll();

    const target = scrollContainer || window;
    target.addEventListener('scroll', handleScroll);
    return () => {
      target.removeEventListener('scroll', handleScroll);
      if (scrollSaveTimer.current) {
        clearTimeout(scrollSaveTimer.current);
      }
    };
  }, []);

  // 获取叶子节点的实时内容
  const getLeafItemContent = (itemId: string): string | undefined => {
    const leafItem = leafItems.find(leaf => leaf.id === itemId);
    return leafItem?.content;
  };

  // 检查是否为叶子节点
  const isLeafNode = (item: OutlineItem): boolean => {
    return !item.children || item.children.length === 0;
  };

  const rememberChapter = (chapterId: string) => {
    setActiveChapterId(chapterId);
    draftStorage.saveDraft({ lastEditedChapterId: chapterId });
  };

  // 渲染目录结构
  const renderOutline = (items: OutlineItem[], level: number = 1): React.ReactElement[] => {
    return items.map((item) => {
      const isLeaf = isLeafNode(item);
      const currentContent = isLeaf ? getLeafItemContent(item.id) : item.content;
      
      return (
        <div
          key={item.id}
          id={isLeaf ? `content-chapter-${item.id}` : undefined}
          tabIndex={isLeaf ? 0 : undefined}
          onClick={() => isLeaf && rememberChapter(item.id)}
          onFocus={() => isLeaf && rememberChapter(item.id)}
          className={`mb-${level === 1 ? '8' : '4'} ${
            isLeaf && activeChapterId === item.id
              ? 'rounded-lg bg-blue-50/50 p-3 ring-1 ring-blue-100'
              : ''
          }`}
        >
          {/* 标题 */}
          <div className={`text-${level === 1 ? 'xl' : level === 2 ? 'lg' : 'base'} font-${level === 1 ? 'bold' : 'semibold'} text-gray-900 mb-2`}>
            {item.id} {item.title}
          </div>
          
          {/* 描述 */}
          <div className="text-sm text-gray-600 mb-4">
            {item.description}
          </div>

          {/* 内容（仅叶子节点） */}
          {isLeaf && (
            <div className="border-l-4 border-blue-200 pl-4 mb-6">
              {currentContent ? (
                <div className="prose max-w-none">
                  <ReactMarkdown>{currentContent}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-gray-400 italic py-4">
                  <DocumentTextIcon className="inline w-4 h-4 mr-2" />
                  {progress.generating.has(item.id) ? (
                    <span className="text-blue-600">正在生成内容...</span>
                  ) : (
                    '内容待生成...'
                  )}
                </div>
              )}
            </div>
          )}

          {/* 子章节 */}
          {item.children && item.children.length > 0 && (
            <div className={`ml-${level * 4} mt-4`}>
              {renderOutline(item.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // 生成单个章节内容
  const generateItemContent = async (item: OutlineItem, projectOverview: string): Promise<OutlineItem> => {
    if (!outlineData) throw new Error('缺少目录数据');
    
    let debugPhase = 'prepare';
    rememberChapter(item.id);
    // 将当前项目添加到正在生成的集合中
    setProgress(prev => ({ 
      ...prev, 
      current: item.title,
      generating: new Set([...Array.from(prev.generating), item.id])
    }));
    
    try {
      // 获取上级章节和同级章节信息
      const parentChapters = getParentChapters(item.id, outlineData.outline);
      const siblingChapters = getSiblingChapters(item.id, outlineData.outline);

      const request: ChapterContentRequest = {
        chapter: item,
        parent_chapters: parentChapters,
        sibling_chapters: siblingChapters,
        project_overview: projectOverview,
        max_length: item.expected_word_count || 500,
        ...settings,
      };

      debugPhase = 'request';
      // #region debug-point A-C:chapter-request
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7781/event', { method: 'POST', body: JSON.stringify({ sessionId: 'real-bid-generation-failure', runId: 'post-fix', hypothesisId: 'A,B,C', location: 'frontend/src/pages/ContentEdit.tsx:generateItemContent', msg: '[DEBUG] Chapter generation request started', data: { chapterId: item.id, title: item.title, maxLength: request.max_length, parentCount: parentChapters.length, siblingCount: siblingChapters.length }, traceId: item.id, ts: Date.now() }) }).catch(() => undefined);
      }
      // #endregion
      const response = await contentApi.generateChapterContentStream(request);
      debugPhase = 'stream';
      // #region debug-point B-D:chapter-response
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7781/event', { method: 'POST', body: JSON.stringify({ sessionId: 'real-bid-generation-failure', runId: 'post-fix', hypothesisId: 'B,C,D', location: 'frontend/src/pages/ContentEdit.tsx:generateItemContent', msg: '[DEBUG] Chapter generation response received', data: { chapterId: item.id, status: response.status, ok: response.ok, contentType: response.headers?.get('content-type') || '' }, traceId: item.id, ts: Date.now() }) }).catch(() => undefined);
      }
      // #endregion

      let content = '';
      const updatedItem = { ...item };

      await collectSseText(
        response,
        (fullText) => {
          content = fullText;
          updatedItem.content = content;
          draftStorage.upsertChapterContent(item.id, content);
          setLeafItems((prevItems) => {
            const newItems = [...prevItems];
            const index = newItems.findIndex((leafItem) => leafItem.id === item.id);
            if (index !== -1) {
              newItems[index] = { ...updatedItem };
            }
            return newItems;
          });
        },
        '章节内容生成失败'
      );

      // #region debug-point D-E:chapter-stream-complete
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7781/event', { method: 'POST', body: JSON.stringify({ sessionId: 'real-bid-generation-failure', runId: 'post-fix', hypothesisId: 'D,E', location: 'frontend/src/pages/ContentEdit.tsx:generateItemContent', msg: '[DEBUG] Chapter stream completed', data: { chapterId: item.id, contentChars: content.length }, traceId: item.id, ts: Date.now() }) }).catch(() => undefined);
      }
      // #endregion
      if (!content.trim()) {
        throw new Error('章节内容为空');
      }
      if (projectId) {
        debugPhase = 'save';
        await projectApi.updateOutlineContent(projectId, item.id, content);
      }
      debugPhase = 'done';
      // #region debug-point E:chapter-save-complete
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7781/event', { method: 'POST', body: JSON.stringify({ sessionId: 'real-bid-generation-failure', runId: 'post-fix', hypothesisId: 'E', location: 'frontend/src/pages/ContentEdit.tsx:generateItemContent', msg: '[DEBUG] Chapter content saved', data: { chapterId: item.id, contentChars: content.length, persisted: Boolean(projectId) }, traceId: item.id, ts: Date.now() }) }).catch(() => undefined);
      }
      // #endregion

      return updatedItem;
    } catch (error) {
      // #region debug-point A-E:chapter-failed
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7781/event', { method: 'POST', body: JSON.stringify({ sessionId: 'real-bid-generation-failure', runId: 'post-fix', hypothesisId: 'A,B,C,D,E', location: 'frontend/src/pages/ContentEdit.tsx:generateItemContent', msg: '[DEBUG] Chapter generation or save failed', data: { chapterId: item.id, phase: debugPhase, errorType: error instanceof Error ? error.name : typeof error, errorMessage: error instanceof Error ? error.message : String(error) }, traceId: item.id, ts: Date.now() }) }).catch(() => undefined);
      }
      // #endregion
      setProgress(prev => ({
        ...prev,
        failed: Array.from(new Set([...prev.failed, item.id]))
      }));
      throw error;
    } finally {
      // 从正在生成的集合中移除当前项目
      setProgress(prev => {
        const newGenerating = new Set(Array.from(prev.generating));
        newGenerating.delete(item.id);
        return {
          ...prev,
          generating: newGenerating
        };
      });
    }
  };

  // 开始生成所有内容
  const handleGenerateContent = async () => {
    if (!outlineData || leafItems.length === 0) return;

    const previousFailures = new Set(progress.failed);
    const pendingItems = leafItems.filter(
      (item) => !item.content || previousFailures.has(item.id)
    );
    const generationItems = pendingItems.length > 0 ? pendingItems : leafItems;
    setIsGenerating(true);
    setMessage(null);
    setProgress({
      total: generationItems.length,
      completed: 0,
      current: '',
      failed: [],
      generating: new Set<string>()
    });

    try {
      // 使用5个并发线程生成内容
      const concurrency = 5;
      const updatedItems = [...leafItems];
      const failedItemIds = new Set<string>();
      
      for (let i = 0; i < generationItems.length; i += concurrency) {
        const batch = generationItems.slice(i, i + concurrency);
        const promises = batch.map(item => 
          generateItemContent(item, outlineData.project_overview || '')
            .then(updatedItem => {
              const index = updatedItems.findIndex(ui => ui.id === updatedItem.id);
              if (index !== -1) {
                updatedItems[index] = updatedItem;
              }
              setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
              return updatedItem;
            })
            .catch(() => {
              failedItemIds.add(item.id);
              setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
              return item; // 返回原始项目
            })
        );

        await Promise.all(promises);
      }

      // 更新状态
      setLeafItems(updatedItems);
      const failedChapterIds = Array.from(failedItemIds);
      setProgress(prev => ({ ...prev, failed: failedChapterIds }));
      draftStorage.saveDraft({ failedChapterIds });
      
      if (failedItemIds.size > 0) {
        setMessage({ type: 'error', text: `${failedItemIds.size} 个章节生成或保存失败，请重试` });
      } else {
        if (projectId) {
          await projectApi.updateFlowStatus(projectId, 40);
        }
        setMessage({ type: 'success', text: '标书正文生成并保存成功' });
      }
      
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '生成内容失败') });
    } finally {
      setIsGenerating(false);
      setProgress(prev => ({ ...prev, current: '', generating: new Set<string>() }));
    }
  };

  // 获取叶子节点的最新内容（包括生成的内容）
  const getLatestContent = (item: OutlineItem): string => {
    if (!item.children || item.children.length === 0) {
      // 叶子节点，从 leafItems 获取最新内容
      const leafItem = leafItems.find(leaf => leaf.id === item.id);
      return leafItem?.content || item.content || '';
    }
    return item.content || '';
  };

  // 解析Markdown内容为Word段落
  // （已提取到文件顶层，供后续导出Word等复用）

  // 滚动到页面顶部
  const scrollToTop = () => {
    const scrollContainer = getContentScrollContainer();
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 导出Word文档
  const handleExportWord = async () => {
    if (!outlineData) return;

    try {
      setMessage(null);
      // 构建带有最新内容的导出数据（leafItems 中存的是实时内容）
      const buildExportOutline = (items: OutlineItem[]): OutlineItem[] => {
        return items.map(item => {
          const latestContent = getLatestContent(item);
          const exportedItem: OutlineItem = {
            ...item,
            content: latestContent,
          };
          if (item.children && item.children.length > 0) {
            exportedItem.children = buildExportOutline(item.children);
          }
          return exportedItem;
        });
      };

      const exportPayload = {
        project_name: resolvedProjectName,
        outline: buildExportOutline(outlineData.outline),
        ...settings,
      };

      // #region debug-point D:export-payload
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7783/event', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: 'export-project-name',
            runId: 'post-fix',
            hypothesisId: 'D',
            location: 'frontend/src/pages/ContentEdit.tsx:handleExportWord',
            msg: '[DEBUG] Word export payload prepared',
            data: {
              projectId: projectId || null,
              projectName: exportPayload.project_name || '',
              outlineCount: exportPayload.outline.length,
            },
            ts: Date.now(),
          }),
        }).catch(() => undefined);
      }
      // #endregion
      const response = await documentApi.exportWord(exportPayload);
      const blob = await response.blob();
      saveAs(blob, `${resolvedProjectName || '标书文档'}.docx`);
      setMessage({ type: 'success', text: '导出成功' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '导出失败，请重试') });
    }
  };

  if (!outlineData) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">暂无内容</h3>
            <p className="mt-1 text-sm text-gray-500">
              请先在"目录编辑"步骤中生成目录结构
            </p>
          </div>
        </div>
      </div>
    );
  }

  const completedItems = leafItems.filter(item => item.content).length;
  const resumableItemCount = leafItems.filter(
    (item) => !item.content || progress.failed.includes(item.id)
  ).length;
  const generationButtonLabel = resumableItemCount > 0
    && resumableItemCount < leafItems.length
    ? `继续生成（${resumableItemCount}章）`
    : completedItems === leafItems.length
      ? '重新生成全部'
      : '生成标书';

  return (
    <div className="max-w-6xl mx-auto">
      {/* 顶部工具栏 */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">标书内容</h2>
              <p className="text-sm text-gray-500 mt-1">
                共 {leafItems.length} 个章节，已生成 {completedItems} 个
                {progress.failed.length > 0 && (
                  <span className="text-red-500 ml-2">失败 {progress.failed.length} 个</span>
                )}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlayIcon className="w-4 h-4 mr-2" />
                {isGenerating ? '生成中...' : generationButtonLabel}
              </button>
              
              <button
                onClick={handleExportWord}
                disabled={isGenerating}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                导出文档
              </button>
            </div>
          </div>
          
          {/* 进度条 */}
          {isGenerating && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>正在生成: {progress.current}</span>
                <span>{progress.completed} / {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {message && (
            <div className={`mt-4 rounded-md border px-4 py-3 text-sm ${
              message.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}>
              {message.text}
            </div>
          )}
        </div>
      </div>

      {/* 文档内容 */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-8">
          <div className="prose max-w-none">
            {/* 文档标题 */}
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              {resolvedProjectName || '投标技术文件'}
            </h1>
            
            {/* 项目概述 */}
            {outlineData.project_overview && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                <h2 className="text-lg font-semibold text-blue-900 mb-2">项目概述</h2>
                <p className="text-blue-800">{outlineData.project_overview}</p>
              </div>
            )}

            {/* 目录结构和内容 */}
            <div className="space-y-8">
              {renderOutline(outlineData.outline)}
            </div>
          </div>
        </div>
      </div>

      {/* 底部统计 */}
      <div className="mt-6 bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-4 h-4 text-green-500 mr-1" />
              <span>已完成: {completedItems}</span>
            </div>
            <div className="flex items-center">
              <DocumentTextIcon className="w-4 h-4 text-gray-400 mr-1" />
              <span>待生成: {leafItems.length - completedItems}</span>
            </div>
            {progress.failed.length > 0 && (
              <div className="flex items-center">
                <ExclamationCircleIcon className="w-4 h-4 text-red-500 mr-1" />
                <span className="text-red-600">失败: {progress.failed.length}</span>
              </div>
            )}
          </div>
          <div>
            <span>总字数: {leafItems.reduce((sum, item) => sum + (item.content?.length || 0), 0)}</span>
          </div>
        </div>
      </div>

      {/* 回到顶部按钮 */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 z-[60]"
          aria-label="回到顶部"
        >
          <ArrowUpIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default ContentEdit;
